import { api } from '../api';

/**
 * Abnahme-Katalog: Epics, User Stories, Akzeptanzkriterien (docs/abnahme-plan.md).
 *
 * Ist das Modul auf dem System nicht freigeschaltet (`ABNAHME_ENABLED`), antwortet
 * das Backend mit 404 — die Oberfläche fragt deshalb zuerst den Status ab.
 */

export type StoryStatus =
  | 'entwurf'
  | 'geplant'
  | 'in_umsetzung'
  | 'bereit_zur_abnahme'
  | 'abgenommen'
  | 'abgelehnt';

export const STORY_STATUS_LABEL: Record<StoryStatus, string> = {
  entwurf: 'Entwurf',
  geplant: 'Geplant',
  in_umsetzung: 'In Umsetzung',
  bereit_zur_abnahme: 'Bereit zur Abnahme',
  abgenommen: 'Abgenommen',
  abgelehnt: 'Abgelehnt',
};

/** Reihenfolge in Filtern und Zählungen — so, wie eine Story sie durchläuft. */
export const STORY_STATUS_ORDER: StoryStatus[] = [
  'entwurf',
  'geplant',
  'in_umsetzung',
  'bereit_zur_abnahme',
  'abgenommen',
  'abgelehnt',
];

export interface AbnahmeStatusDto {
  enabled: boolean;
  /** Adresse des Testsystems — dort wird geprüft, hier abgezeichnet. */
  testsystemUrl?: string | null;
  status: StoryStatus[];
  transitions: Record<StoryStatus, StoryStatus[]>;
  areas: Array<{ key: string; label: string }>;
}

export interface StoryTicketStandDto {
  /** Tickets, an denen noch gearbeitet wird (neu, triagiert, in Arbeit), beide Systeme. */
  open: number;
  /** Blocker unter den offenen — ein Blocker im Nachtest zählt nicht. */
  blocker: number;
  /** Fix ausgerollt, Nachtest steht aus. */
  nachtest?: number;
  /** Nachtest in Ordnung, wartet nur noch auf „erledigt“. */
  behoben?: number;
  /** Zurückgestellt — bremst die Freigabe einer Story nicht. */
  backlog?: number;
  /** Nummern der nicht abgeschlossenen Tickets dieses Systems (verlinkbar). */
  numbers: number[];
  /** Anzeigenummern der offenen Tickets, z. B. „L-0004", „T-0013". */
  refs: string[];
  /** Anzeigenummern je Phase. */
  phasen?: { offen: string[]; nachtest: string[]; behoben: string[]; backlog?: string[] };
}

/** Abdeckung einer Story durch getaggte Tests der jüngsten Läufe. */
export interface StoryTestsDto {
  kriterien: number;
  /** Kriterien mit mindestens einem Test. */
  abgedeckt: number;
  tests: number;
  fehlgeschlagen: number;
  status: 'gruen' | 'rot' | 'keine';
}

/** Ergebnis eines getaggten Tests im jüngsten Lauf seiner Art. */
export interface TestTrefferDto {
  ref: string;
  name: string;
  status: 'bestanden' | 'fehlgeschlagen' | 'uebersprungen' | 'blockiert';
  art: string;
  laufId: number;
  meldung: string | null;
}

export type LaufArt = 'ci_backend' | 'ci_frontend' | 'e2e_smoke' | 'manuell';

export const LAUF_ART_LABEL: Record<LaufArt, string> = {
  ci_backend: 'Backend',
  ci_frontend: 'Frontend',
  e2e_smoke: 'E2E-Smoke',
  manuell: 'Manuell',
};

export interface LaufDto {
  id: number;
  art: LaufArt;
  umgebung: 'test' | 'live';
  branch: string | null;
  commit: string | null;
  version: string | null;
  runUrl: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  bestanden: number;
  fehlgeschlagen: number;
  uebersprungen: number;
  coverage: number | null;
  createdAt: string | null;
  titel?: string | null;
  gestartetVon?: string | null;
  abgeschlossenAt?: string | null;
}

export type ManuellStatus = 'offen' | 'bestanden' | 'fehlgeschlagen' | 'blockiert' | 'uebersprungen';

export const MANUELL_STATUS_LABEL: Record<ManuellStatus, string> = {
  offen: 'Offen',
  bestanden: 'Bestanden',
  fehlgeschlagen: 'Fehlgeschlagen',
  blockiert: 'Blockiert',
  uebersprungen: 'Übersprungen',
};

export interface TestfallDto {
  id: number;
  storyId: number;
  kriteriumId: number | null;
  titel: string;
  schritte: string;
  erwartung: string;
  herkunft: string | null;
  archived: boolean;
}

export interface FreigabeDto {
  id: number;
  storyId: number;
  storyKey: string;
  entscheidung: 'abgenommen' | 'abgelehnt';
  begruendung: string | null;
  version: string | null;
  name: string | null;
  ticketRef: string | null;
  createdAt: string | null;
}

export interface FreigabeStoryDto {
  id: number;
  key: string;
  title: string;
  description: string;
  status: StoryStatus;
  readyVersion: string | null;
  epic: { key: string; title: string };
  kriterien: Array<{
    key: string;
    text: string;
    anleitung?: PruefAnleitungDto | null;
    pruefung?: KriteriumPruefungDto;
  }>;
  tests: StoryTestsDto;
  tickets: StoryTicketStandDto;
  letzteFreigabe: FreigabeDto | null;
}

/** Wie ein Kriterium geprüft ist — automatisch, von Hand oder noch gar nicht. Rot gewinnt. */
export interface KriteriumPruefungDto {
  status: 'keine' | 'gruen' | 'rot';
  automatisch: number;
  automatischRot: number;
  manuell: { status: string; von: string | null; am: string | null; meldung: string | null } | null;
}

export interface FreigabeUebersichtDto {
  bereit: FreigabeStoryDto[];
  entschieden: FreigabeStoryDto[];
  testsystemUrl: string | null;
}

export interface LaufErgebnisDto {
  id?: number;
  testfallId?: number | null;
  ticketRef?: string | null;
  erfasstVon?: string | null;
  erfasstAt?: string | null;
  testfall?: TestfallDto;
  ref: string;
  name: string;
  storyKey: string | null;
  status: string;
  dauerMs: number | null;
  meldung: string | null;
}

export interface LaeufeDto {
  items: LaufDto[];
  /** Tests, deren Schlüssel es im Katalog nicht (mehr) gibt. */
  verwaist: Array<{ key: string; tests: string[] }>;
  /** Getaggte Tests, die in den letzten Läufen mal grün, mal rot waren. */
  instabil: Array<{ art: LaufArt; ref: string }>;
}

/** Phase eines Tickets aus Sicht der Abnahme — dieselbe Einteilung wie im Backend. */
export type TicketPhase = 'offen' | 'nachtest' | 'behoben' | 'backlog' | 'abgeschlossen';

export function ticketPhase(status: string): TicketPhase {
  if (status === 'auf_testsystem') return 'nachtest';
  if (status === 'nachtest_ok') return 'behoben';
  if (status === 'erledigt' || status === 'abgelehnt' || status === 'duplikat') return 'abgeschlossen';
  // Zurückgestellt ist keine laufende Arbeit — sonst färbt es eine Story rot,
  // an der gerade niemand arbeitet und auch keiner arbeiten will.
  if (status === 'backlog') return 'backlog';
  return 'offen';
}

export interface EpicDto {
  id: number;
  key: string;
  title: string;
  description: string;
  area: string | null;
  position: number;
  archived: boolean;
}

export interface StoryDto {
  id: number;
  epicId: number;
  /** Fester Schlüssel, z. B. „PERS-3" — ändert sich nie. */
  key: string;
  section: string | null;
  title: string;
  status: StoryStatus;
  readyVersion: string | null;
  readyCommit: string | null;
  position: number;
  archived: boolean;
  updatedAt: string | null;
  kriterienCount?: number;
  tickets: StoryTicketStandDto;
  tests?: StoryTestsDto;
  /** Abgenommen, inzwischen aber rot oder mit Blocker-Ticket. */
  regression?: boolean;
}

export interface KatalogDto {
  epics: Array<EpicDto & { stories: StoryDto[] }>;
  counts: Record<StoryStatus, number>;
  total: number;
  /** Ticket-Verweise auf Schlüssel, die es im Katalog nicht (mehr) gibt. */
  orphanTicketKeys: string[];
  ticketPrefix: string;
  /** Wann das Testsystem zuletzt seinen Ticket-Stand geschickt hat. */
  testsystem: { receivedAt: string | null };
  /** Jüngster Lauf je Art. */
  letzteLaeufe?: Partial<Record<LaufArt, LaufDto>>;
  verwaisteTests?: number;
}

/** Anleitung zum Prüfen eines Kriteriums von Hand (docs/abnahme-plan.md, Abschnitt 13). */
export interface PruefAnleitungDto {
  wo: 'Website' | 'Kundenbereich' | 'Office' | 'Lehrerportal' | 'E-Mail-Postfach' | 'Technik';
  vorbereitung: string | null;
  schritte: string[];
  erwartung: string;
  hinweis: string | null;
  quelle?: string;
}

export interface KriteriumDto {
  id: number;
  storyId: number;
  /** Fester Schlüssel, z. B. „PERS-3.2" — Tests taggen damit. */
  key: string;
  text: string;
  anleitung?: PruefAnleitungDto | null;
  position: number;
  archived: boolean;
  tests?: TestTrefferDto[];
}

export interface StoryHistoryDto {
  id: number;
  type: string;
  description: string;
  details: string | null;
  actorName: string | null;
  createdAt: string | null;
}

export interface StoryTicketDto {
  id: number;
  number: number;
  label: string;
  title: string;
  status: string;
  severity: string;
}

/** Offenes Ticket vom Testsystem — ein Abbild, nicht verlinkbar. */
export interface FremdTicketDto {
  source: string;
  label: string;
  title: string;
  status: string;
  severity: string;
  receivedAt: string | null;
}

export interface StoryAuswahlDto {
  /** `live`: Spiegel des Live-Katalogs; `local`: eigener Katalog. */
  source: 'live' | 'local';
  syncedAt: string | null;
  items: Array<{ key: string; title: string; status: StoryStatus; epicKey: string; epicTitle: string }>;
}

export interface StoryDetailDto extends StoryDto {
  description: string;
  allowedStatus: StoryStatus[];
  createdAt: string | null;
  epic: EpicDto;
  kriterien: KriteriumDto[];
  ticketList: StoryTicketDto[];
  fremdTickets: FremdTicketDto[];
  history: StoryHistoryDto[];
  /** Tests, die direkt die Story (nicht ein Kriterium) tragen. */
  storyTests?: TestTrefferDto[];
  freigaben?: FreigabeDto[];
  testfaelle?: TestfallDto[];
}

export interface StoryPatchInput {
  title?: string;
  description?: string;
  section?: string | null;
  epicId?: number;
  status?: StoryStatus;
  readyVersion?: string;
  readyCommit?: string;
  position?: number;
  archived?: boolean;
}

export interface EpicInput {
  key?: string;
  title?: string;
  description?: string;
  area?: string | null;
  position?: number;
  archived?: boolean;
}

export const abnahmeApi = {
  status: () => api.get<AbnahmeStatusDto>('/api/abnahme/status'),

  /** Stories für die Ticket-Zuordnung — am Ticket-Recht, nicht am Katalog-Recht. */
  storyAuswahl: () => api.get<StoryAuswahlDto>('/api/testing/stories'),

  katalog: (includeArchived = false) =>
    api.get<KatalogDto>('/api/abnahme/katalog', {
      params: includeArchived ? { includeArchived: 'true' } : {},
    }),

  erstbefuellung: () =>
    api.post<{ epics: number; stories: number }>('/api/abnahme/katalog/erstbefuellung', {}),

  exportieren: () => api.get<Record<string, unknown>>('/api/abnahme/katalog/export'),

  importieren: (daten: Record<string, unknown>) =>
    api.post<{ epics: number; stories: number; kriterien: number }>(
      '/api/abnahme/katalog/import',
      daten,
    ),

  epicAnlegen: (input: EpicInput & { key: string; title: string }) =>
    api.post<EpicDto>('/api/abnahme/epics', input),

  epicAendern: (id: number, input: EpicInput) =>
    api.patch<EpicDto>(`/api/abnahme/epics/${id}`, input),

  storyAnlegen: (input: { epicId: number; title: string; description?: string; status?: StoryStatus }) =>
    api.post<StoryDetailDto>('/api/abnahme/stories', input),

  story: (id: number) => api.get<StoryDetailDto>(`/api/abnahme/stories/${id}`),

  storyAendern: (id: number, input: StoryPatchInput) =>
    api.patch<StoryDetailDto>(`/api/abnahme/stories/${id}`, input),

  kriteriumAnlegen: (storyId: number, text: string) =>
    api.post<KriteriumDto>(`/api/abnahme/stories/${storyId}/kriterien`, { text }),

  kriteriumAendern: (
    id: number,
    input: {
      text?: string;
      position?: number;
      archived?: boolean;
      anleitung?: Omit<PruefAnleitungDto, 'quelle'> | null;
    },
  ) =>
    api.patch<KriteriumDto>(`/api/abnahme/kriterien/${id}`, input),

  laeufe: (art?: LaufArt, limit = 50) =>
    api.get<LaeufeDto>('/api/abnahme/laeufe', {
      params: { ...(art ? { art } : {}), limit: String(limit) },
    }),

  lauf: (id: number) =>
    api.get<LaufDto & { ergebnisse: LaufErgebnisDto[] }>(`/api/abnahme/laeufe/${id}`),

  freigabe: () => api.get<FreigabeUebersichtDto>('/api/abnahme/freigabe'),

  entscheiden: (storyId: number, entscheidung: 'abgenommen' | 'abgelehnt', begruendung?: string) =>
    api.post<FreigabeDto>(`/api/abnahme/stories/${storyId}/freigabe`, { entscheidung, begruendung }),

  testfallAnlegen: (
    storyId: number,
    input: { titel: string; schritte?: string; erwartung?: string; kriteriumId?: number | null },
  ) => api.post<TestfallDto>(`/api/abnahme/stories/${storyId}/testfaelle`, input),

  testfallAendern: (
    id: number,
    input: { titel?: string; schritte?: string; erwartung?: string; kriteriumId?: number | null; archived?: boolean },
  ) => api.patch<TestfallDto>(`/api/abnahme/testfaelle/${id}`, input),

  e2eTestfaelleUebernehmen: () =>
    api.post<{ angelegt: number; ohneStory: string[] }>('/api/abnahme/testfaelle/e2e-uebernehmen', {}),

  manuellerLauf: (input: { titel: string; storyIds?: number[]; version?: string }) =>
    api.post<LaufDto>('/api/abnahme/laeufe/manuell', input),

  ergebnisErfassen: (
    id: number,
    input: { status: ManuellStatus; meldung?: string; ticketAnlegen?: boolean },
  ) => api.patch<LaufErgebnisDto>(`/api/abnahme/ergebnisse/${id}`, input),

  /** Ein Kriterium in der Freigabe abhaken; zählt wie ein manuelles Testergebnis. */
  kriteriumPruefen: (
    key: string,
    input: { status: 'bestanden' | 'fehlgeschlagen'; meldung?: string; ticketAnlegen?: boolean },
  ) => api.post<LaufErgebnisDto>(`/api/abnahme/kriterien/${encodeURIComponent(key)}/pruefen`, input),

  laufAbschliessen: (id: number) => api.post<LaufDto>(`/api/abnahme/laeufe/${id}/abschliessen`, {}),

  statistik: () => api.get<StatistikDto>('/api/abnahme/statistik'),

  report: (stichtag?: string) =>
    api.get<ReportDto>(stichtag ? `/api/abnahme/report?stichtag=${stichtag}` : '/api/abnahme/report'),
};

// ------------------------------------------------ Statistik & Report (Abschnitt 11)

export interface TicketKennzahlenDto {
  /** Neu, triagiert, in Arbeit — nur das zählt als offen. */
  offen: number;
  /** Offene Blocker. Ein zurückgestellter Blocker blockiert gerade nichts. */
  blocker: number;
  nachtest: number;
  behoben: number;
  /** Zurückgestellt: weder offen noch abgeschlossen, und in keiner Dauer enthalten. */
  backlog?: number;
  abgeschlossen: number;
}

export interface EpicKennzahlDto {
  key: string;
  title: string;
  gesamt: number;
  status: Record<StoryStatus, number>;
  /** Anteil geprüfter Kriterien in Prozent; null ohne Kriterien oder in rekonstruierten Tagen. */
  abdeckung: number | null;
}

export interface KennzahlenDto {
  stories: { gesamt: number; status: Record<StoryStatus, number> };
  epics: EpicKennzahlDto[];
  tickets: TicketKennzahlenDto;
  blockerListe: { label: string; titel: string; story: string | null }[];
  abdeckung: { kriterien: number; abgedeckt: number; prozent: number | null } | null;
  laeufe: Partial<Record<LaufArt, { bestanden: number; fehlgeschlagen: number; coverage: number | null }>>;
  regressionen: string[];
}

export interface SnapshotDto {
  tag: string;
  rekonstruiert: boolean;
  stories: KennzahlenDto['stories'] | null;
  tickets: TicketKennzahlenDto | null;
  abdeckung: KennzahlenDto['abdeckung'];
}

export interface ReleaseDto {
  id: number;
  umgebung: 'test' | 'live';
  version: string | null;
  commit: string;
  commits: number;
  /** „T-0013“ (Testsystem) oder „L-0004“ (live). */
  tickets: string[];
  runUrl: string | null;
  deployedAt: string;
  /** Stories, die seit dem vorigen Deploy derselben Umgebung abgenommen wurden. */
  abgenommen: string[];
}

export interface StatistikDto {
  aktuell: KennzahlenDto;
  snapshots: SnapshotDto[];
  wochen: { woche: string; neu: number; geschlossen: number }[];
  durchlaufzeit: {
    bisNachtestTage: number | null;
    bisErledigtTage: number | null;
    anzahlNachtest: number;
    anzahlErledigt: number;
  };
  nachtest: { gesamt: number; zurueck: number; quote: number | null };
  laeufe: { art: LaufArt; am: string; bestanden: number; fehlgeschlagen: number; coverage: number | null }[];
  releases: ReleaseDto[];
}

export interface ReportDto {
  stichtag: string;
  quelle: { art: 'aktuell' | 'schnappschuss'; tag: string; rekonstruiert: boolean };
  kennzahlen: KennzahlenDto;
  releases: ReleaseDto[];
  zuletztAbgenommen: { key: string; title: string; name: string | null; version: string | null; am: string }[];
  snapshots: SnapshotDto[];
}

